import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ContractSelector } from './components/ContractSelector';
import { AIWorkspaceArea } from './components/AIWorkspaceArea';
import { MetadataPanel } from './components/MetadataPanel';
import { ActionBar } from './components/ActionBar';
import { InsertToContractModal } from './components/InsertToContractModal';
import { useLegalAI } from '../../../hooks/useLegalAI';
import { createContractContext, addAINoteToContract } from '../../../services/legalAIService';
import { SelectedContract } from './types';
import { GlobalNavigation } from '../../common/GlobalNavigation';

export const LegalAssistanceWorkspace: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [selectedContract, setSelectedContract] = useState<SelectedContract | null>(null);
  const [activeClause, setActiveClause] = useState<string | null>(null);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<'contracts' | 'chat' | 'details'>('chat');

  // Insert to Contract modal state
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertContent, setInsertContent] = useState('');

  // Legal AI hook
  const legalAI = useLegalAI({
    mode: 'standard',
    contractContext: selectedContract ? createContractContext(selectedContract) : undefined
  });

  // Update contract context when selection changes
  useEffect(() => {
    if (selectedContract) {
      legalAI.setContractContext(createContractContext(selectedContract));
    }
  }, [selectedContract]);

  // Handlers
  const handleContractSelect = useCallback((contract: SelectedContract) => {
    setSelectedContract(contract);
    setActiveClause(null);
    legalAI.clearConversation();
    // On mobile, switch to chat view
    setMobilePanel('chat');
  }, [legalAI]);

  const handleClauseSelect = useCallback((clauseText: string) => {
    setActiveClause(clauseText);
  }, []);

  const handleInsertToContract = useCallback(() => {
    if (!legalAI.lastResponse) return;
    setInsertContent(legalAI.lastResponse.content);
    setShowInsertModal(true);
  }, [legalAI.lastResponse]);

  const handleDoInsert = useCallback(async (content: string, asNote: boolean) => {
    if (!selectedContract || !asNote) return;
    await addAINoteToContract(selectedContract._id, content, 'legal-ai');
  }, [selectedContract]);

  const handleAddComment = useCallback((text: string) => {
    // TODO: Implement add as comment
    console.log('Add comment:', text);
    alert('This feature will add the AI response as a comment. Coming soon!');
  }, []);

  const handleExport = useCallback(() => {
    // Export conversation as markdown
    const messages = legalAI.conversationHistory
      .filter(m => m.role !== 'system')
      .map(m => `## ${m.role === 'user' ? 'You' : 'AI Assistant'}\n\n${m.content}`)
      .join('\n\n---\n\n');

    const contractInfo = selectedContract
      ? `# Legal AI Conversation\n\n**Contract:** ${selectedContract.title}\n**Supplier:** ${selectedContract.supplierName}\n**Date:** ${new Date().toLocaleDateString()}\n\n---\n\n`
      : `# Legal AI Conversation\n\n**Date:** ${new Date().toLocaleDateString()}\n\n---\n\n`;

    const fullContent = contractInfo + messages;

    const blob = new Blob([fullContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legal-ai-conversation-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [legalAI.conversationHistory, selectedContract]);

  const handleClearConversation = useCallback(() => {
    if (window.confirm('Are you sure you want to clear the conversation? This cannot be undone.')) {
      legalAI.clearConversation();
    }
  }, [legalAI]);

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Global Navigation */}
      <GlobalNavigation
        title="Legal AI Assistant"
        showBackButton={true}
        onBack={handleBack}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Legal AI Assistant' }
        ]}
      />

      {/* Mobile Tab Bar */}
      <div className="lg:hidden bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="flex">
          <button
            onClick={() => setMobilePanel('contracts')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mobilePanel === 'contracts'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Contracts
          </button>
          <button
            onClick={() => setMobilePanel('chat')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mobilePanel === 'chat'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            AI Chat
          </button>
          <button
            onClick={() => setMobilePanel('details')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mobilePanel === 'details'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Details
          </button>
        </div>
      </div>

      {/* Main Content - Three Panel Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Contract Selector */}
        <div
          className={`
            ${mobilePanel === 'contracts' ? 'block' : 'hidden'}
            lg:block
            ${leftPanelCollapsed ? 'lg:w-0' : 'lg:w-72'}
            w-full lg:flex-shrink-0
            border-r border-gray-200 dark:border-dark-border
            bg-white dark:bg-dark-surface
            overflow-hidden transition-all duration-300
          `}
        >
          {!leftPanelCollapsed && (
            <ContractSelector
              selectedContract={selectedContract}
              onSelectContract={handleContractSelect}
            />
          )}
        </div>

        {/* Toggle for left panel - Desktop only */}
        <button
          onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          className="hidden lg:flex items-center justify-center w-6 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border-r border-gray-200 dark:border-dark-border transition-colors"
          title={leftPanelCollapsed ? 'Show contracts' : 'Hide contracts'}
        >
          {leftPanelCollapsed ? (
            <Menu className="w-4 h-4 text-gray-500" />
          ) : (
            <ArrowLeft className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {/* Center Panel - AI Workspace */}
        <div
          className={`
            ${mobilePanel === 'chat' ? 'flex' : 'hidden'}
            lg:flex
            flex-1 flex-col overflow-hidden
          `}
        >
          <AIWorkspaceArea
            legalAI={legalAI}
            activeClause={activeClause}
            onClauseSelect={handleClauseSelect}
            hasContract={!!selectedContract}
            contractId={selectedContract?._id}
          />
        </div>

        {/* Toggle for right panel - Desktop only */}
        <button
          onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
          className="hidden lg:flex items-center justify-center w-6 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border-l border-gray-200 dark:border-dark-border transition-colors"
          title={rightPanelCollapsed ? 'Show details' : 'Hide details'}
        >
          {rightPanelCollapsed ? (
            <Menu className="w-4 h-4 text-gray-500" />
          ) : (
            <X className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {/* Right Panel - Metadata & Context */}
        <div
          className={`
            ${mobilePanel === 'details' ? 'block' : 'hidden'}
            lg:block
            ${rightPanelCollapsed ? 'lg:w-0' : 'lg:w-80'}
            w-full lg:flex-shrink-0
            border-l border-gray-200 dark:border-dark-border
            bg-white dark:bg-dark-surface
            overflow-hidden transition-all duration-300
          `}
        >
          {!rightPanelCollapsed && (
            <MetadataPanel
              contract={selectedContract}
              mode={legalAI.mode}
              provider={legalAI.provider}
              onModeChange={legalAI.setMode}
              onProviderChange={legalAI.setProvider}
            />
          )}
        </div>
      </div>

      {/* Bottom Action Bar - Hidden on mobile when not in chat */}
      <div className={`${mobilePanel !== 'chat' ? 'hidden lg:block' : ''}`}>
        <ActionBar
          onInsert={handleInsertToContract}
          onAddComment={handleAddComment}
          onExport={handleExport}
          onClear={handleClearConversation}
          disabled={!legalAI.lastResponse}
          hasMessages={legalAI.conversationHistory.length > 0}
        />
      </div>

      {/* Legal Disclaimer Footer */}
      <footer className="bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 px-6 py-2">
        <p className="text-xs text-amber-800 dark:text-amber-300 text-center">
          AI-generated suggestions require professional legal review before implementation.
          This tool provides guidance only and does not constitute legal advice.
        </p>
      </footer>

      {/* Insert to Contract Modal */}
      <InsertToContractModal
        isOpen={showInsertModal}
        onClose={() => setShowInsertModal(false)}
        onInsert={handleDoInsert}
        content={insertContent}
        contractTitle={selectedContract?.title || 'Contract'}
      />
    </div>
  );
};

export default LegalAssistanceWorkspace;
